// src/components/shared/Toast.jsx
// Substitui alert() por notificações não-bloqueantes.
//
// USO (via hook):
//   const { toast, ToastContainer } = useToast();
//
//   // No JSX:
//   <ToastContainer />
//
//   // Na função:
//   toast.success('Evento agendado com sucesso!');
//   toast.error('Erro ao remover evento: ' + mensagem);
//   toast.info('Processando...');

import React, { useState, useCallback, useRef } from 'react';

const ICONS = {
  success: '✅',
  error:   '❌',
  warning: '⚠️',
  info:    'ℹ️',
};

const COLORS = {
  success: { bg: 'rgba(var(--success-rgb), 0.15)',   border: 'rgba(var(--success-rgb), 0.4)',   text: 'var(--success)' },
  error:   { bg: 'rgba(var(--error-rgb), 0.15)',     border: 'rgba(var(--error-rgb), 0.4)',     text: 'var(--error)' },
  warning: { bg: 'rgba(var(--warning-rgb), 0.15)',   border: 'rgba(var(--warning-rgb), 0.4)',   text: 'var(--warning)' },
  info:    { bg: 'rgba(var(--brand-rgb), 0.15)',  border: 'rgba(var(--brand-rgb), 0.4)',  text: 'var(--brand-400)' },
};

const DEFAULT_DURATION = {
  success: 3000,
  error:   5000,
  warning: 4000,
  info:    3000,
};

// ─────────────────────────────────────────
// COMPONENTE INDIVIDUAL
// ─────────────────────────────────────────

function ToastItem({ id, type, message, onClose }) {
  const c = COLORS[type] || COLORS.info;

  return (
    <div style={{
      display:      'flex',
      alignItems:   'flex-start',
      gap:          '10px',
      padding:      '14px 16px',
      background:   c.bg,
      border:       `1px solid ${c.border}`,
      borderRadius: '10px',
      boxShadow:    'none',
      maxWidth:     '380px',
      animation:    'toastIn 0.25s ease-out',
      color:        'var(--text-primary)',
    }}>
      <span style={{ fontSize: '18px', flexShrink: 0 }}>{ICONS[type]}</span>
      <span style={{ flex: 1, fontSize: '14px', lineHeight: 1.5 }}>{message}</span>
      <button
        onClick={() => onClose(id)}
        style={{
          background: 'none',
          border:     'none',
          color:      'var(--text-secondary)',
          cursor:     'pointer',
          fontSize:   '16px',
          padding:    '0 0 0 8px',
          flexShrink: 0,
        }}
      >✕</button>
    </div>
  );
}

// ─────────────────────────────────────────
// CONTAINER
// ─────────────────────────────────────────

function ToastContainerComponent({ toasts, onClose }) {
  if (!toasts.length) return null;

  return (
    <div style={{
      position:      'fixed',
      bottom:        '24px',
      right:         '24px',
      zIndex:        9998,
      display:       'flex',
      flexDirection: 'column',
      gap:           '10px',
      alignItems:    'flex-end',
    }}>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(20px) }
          to   { opacity: 1; transform: translateX(0) }
        }
      `}</style>
      {toasts.map(t => (
        <ToastItem key={t.id} {...t} onClose={onClose} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const addToast = useCallback((type, message, duration) => {
    const id  = ++counterRef.current;
    const dur = duration ?? DEFAULT_DURATION[type] ?? 3000;

    setToasts(prev => [...prev, { id, type, message }]);

    if (dur > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, dur);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg, dur) => addToast('success', msg, dur),
    error:   (msg, dur) => addToast('error',   msg, dur),
    warning: (msg, dur) => addToast('warning', msg, dur),
    info:    (msg, dur) => addToast('info',    msg, dur),
  };

  const ToastContainer = useCallback(() => (
    <ToastContainerComponent toasts={toasts} onClose={removeToast} />
  ), [toasts, removeToast]);

  return { toast, ToastContainer };
}

export default ToastContainerComponent;
